import React from "react";
import { graphql, Link } from "gatsby";
import Page from "~/models/page";
import News from "~/models/news";
import Collection from "~/models/collection";
import SEO from "~/views/components/generic/seo";
import structuredData from "~/helpers/structured-data";
import Frame from "~/views/layout/frame";
import Container from "~/views/layout/container";
import Topic from "~/views/layout/topic";
import Header from "~/views/compositions/navigation/header";
import Featured from "~/views/components/article/featured";
import Article from "~/models/article";
import Event from "~/models/event";
import Section from "~/views/layout/section";
import Button from "~/views/elements/button/link";
import ArticleCard from "~/views/components/article/card";
import NewsCard from "~/views/components/news/card";
import EventCard from "~/views/components/event/card";
import PopularArticles from "~/views/compositions/article/popular";
import FeaturedEvents from "~/views/compositions/event/featured";
import NewsletterSubscription from "~/views/components/newsletter/subscription";
import Tagline from "~/views/components/generic/tagline";
import Kuro from "~/views/components/generic/kuro";
import JobPost from "~/models/job-post";
import JobPostCard from "~/views/components/job/job-post-card";

type Props = {
  data: any;
};

const HomePage = ({ data }: Props) => {
  const latestArticles = data.latestArticles.nodes.map(
    (article) => new Article(article as any)
  );
  const latestNews = data.latestNews.nodes.map((news) => new News(news as any));
  const social = new Collection(data.social as any);
  const lifestyle = new Collection(data.lifestyle as any);
  const workstyle = new Collection(data.workstyle as any);
  const design = new Collection(data.design as any);
  const events = data.events.nodes.map((event) => new Event(event as any));
  const jobPosts = data.jobPosts.nodes
    .filter(
      (jobPost: any) =>
        !jobPost.frontmatter.closed && !jobPost.frontmatter.isAutoGenerated
    )
    .map((jobPost: any) => new JobPost(jobPost as any));

  function onClickJobListingApplication(
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) {
    if (typeof window !== "undefined") {
      (window as any).dataLayer.push({
        event: "click_job_listing_application",
      });
    }
  }

  return (
    <Frame>
      <Header />

      <Tagline />

      <Container className="pt-5 sm:pt-12">
        <Featured />
      </Container>

      <div className="hidden sm:block relative">
        <Kuro className="" />
      </div>

      <Container className="py-container">
        <div>
          <div className="mb-8 sm:mb-12 flex justify-between items-center">
            <div>
              <h2 className="ml-[2px] mb-3 sm:mb-4 pl-1 border-l-2 border-black text-2xs font-semibold leading-[1]">
                デジタルプロダクトデザイナー求人
              </h2>
              <p className="font-extrabold text-8xl sm:text-10xl text-neutral-90 leading-[1.2]">
                Featured Jobs
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 w740:grid-cols-2 w1340:grid-cols-3 gap-x-4 sm:gap-x-7 grid-gap-y">
            {jobPosts.map((jobPost) => (
              <JobPostCard
                key={jobPost.id}
                jobPost={jobPost}
                className="[&:nth-child(3)]:hidden w1340:[&:nth-child(3)]:flex"
              />
            ))}
          </div>
        </div>
      </Container>

      <Button label="すべてのデザイナー求人を見る" to="/jobs/">
        すべてのデザイナー求人を見る
      </Button>

      <Container className="pt-14 sm:pt-[60px] pb-container grid grid-cols-12 grid-gap-x">
        <Section
          title="最新のオリジナル記事"
          label="Latest Articles"
          className="col-start-1 col-end-13 lg:col-end-10">
          <ul className="grid sm:grid-cols-3 grid-gap-x grid-gap-y">
            {latestArticles.map((article) => (
              <li key={article.id}>
                <ArticleCard article={article} />
              </li>
            ))}
          </ul>
        </Section>

        <div className="hidden lg:block col-start-10 col-end-13">
          <PopularArticles className="mb-10" />
          <FeaturedEvents />
        </div>
      </Container>

      <Button label="すべての記事を見る" to="/articles/">
        すべての記事を見る
      </Button>

      <Container className="pt-container grid grid-cols-12 grid-gap-x lg:hidden">
        <div className="block col-start-1 col-end-13">
          <PopularArticles />
        </div>
      </Container>

      <Container className="py-container">
        <Section title="最新ニュース" label="Latest News">
          <ul className="grid grid-cols-1 w740:grid-cols-2 w1340:grid-cols-3 gap-x-4 sm:gap-x-7 grid-gap-y">
            {latestNews.map((news) => (
              <li key={news.id} className="w740:last:hidden w1340:last:block">
                <NewsCard news={news} />
              </li>
            ))}
          </ul>
        </Section>
      </Container>

      <Button label="すべてのニュースを見る" to="/news/">
        すべてのニュースを見る
      </Button>

      <Topic
        title="より良い未来の探求"
        subtitle="社会的インパクトを持ったデジタルサービスや他の領域での事業、海外の取り組みの紹介、デザインに関する考察。"
        className="mb-14 sm:mb-0"
        link="/tags/exploring-a-better-future/">
        <ul className="grid sm:grid-cols-3 md:grid-cols-4 grid-gap-x grid-gap-y">
          {social.items.map((article) => (
            <li key={article.id} className="sm:last:hidden md:last:block">
              <ArticleCard article={article} />
            </li>
          ))}
        </ul>
      </Topic>

      <Button
        label="「より良い未来の探求」の記事一覧"
        to="/tags/exploring-a-better-future/"
        className="sm:hidden">
        「より良い未来の探求」の記事一覧
      </Button>

      <Topic
        title="働くを楽しむ"
        subtitle="「より良い未来」をつくるデザイナーの仕事をもっと楽しめるようなアイデアを紹介。"
        className="mb-14 sm:mb-0"
        link="/tags/workstyle/">
        <ul className="grid sm:grid-cols-3 md:grid-cols-4 grid-gap-x grid-gap-y">
          {workstyle.items.map((article) => (
            <li key={article.id} className="sm:last:hidden md:last:block">
              <ArticleCard article={article} />
            </li>
          ))}
        </ul>
      </Topic>

      <Button
        label="「働くを楽しむ」の記事一覧"
        to="/tags/workstyle/"
        className="sm:hidden">
        「働くを楽しむ」の記事一覧
      </Button>

      <Topic
        title="未来をつくる"
        subtitle="近い未来である「明日」に目を向けて、日々の業務に役立つTipsやHow-to、便利なデザインリソースを紹介。"
        className="mb-14 sm:mb-0"
        link="/tags/shaping-a-better-future/">
        <ul className="grid sm:grid-cols-3 md:grid-cols-4 grid-gap-x grid-gap-y">
          {design.items.map((article) => (
            <li key={article.id} className="sm:last:hidden md:last:block">
              <ArticleCard article={article} />
            </li>
          ))}
        </ul>
      </Topic>

      <Button
        label="「未来をつくる」の記事一覧"
        to="/tags/shaping-a-better-future/"
        className="sm:hidden">
        「未来をつくる」の記事一覧
      </Button>

      <Topic
        title="文化と生活"
        subtitle="デザイナーのライフスタイルに焦点を当てて『unprinted』的な視点で暮らしのアイデアを紹介。"
        className="mb-14 sm:mb-0"
        link="/tags/culture-and-lifestyle/">
        <ul className="grid sm:grid-cols-3 md:grid-cols-4 grid-gap-x grid-gap-y">
          {lifestyle.items.map((article) => (
            <li key={article.id} className="sm:last:hidden md:last:block">
              <ArticleCard article={article} />
            </li>
          ))}
        </ul>
      </Topic>

      <Button
        label="「文化と生活」の記事一覧"
        to="/tags/culture-and-lifestyle/"
        className="sm:hidden">
        「文化と生活」の記事一覧
      </Button>

      <Container className="py-container">
        <Section
          title="デジタルデザイン関連のイベント情報"
          label="Upcoming Events">
          <ul className="grid grid-cols-1 w740:grid-cols-2 w1340:grid-cols-3 gap-x-4 sm:gap-x-7 grid-gap-y">
            {events.map((event) => (
              <li key={event.id} className="w740:last:hidden w1340:last:block">
                <EventCard event={event} />
              </li>
            ))}
          </ul>
        </Section>
      </Container>

      <Button label="すべての開催予定イベントを見る" to="/events/">
        すべての開催予定イベントを見る
      </Button>

      <NewsletterSubscription type="light" />
    </Frame>
  );
};

export default HomePage;

export const Head = ({ data }: Props) => {
  const page = new Page(data.page as any);
  const sd = structuredData(page);

  return <SEO page={page} sd={sd} />;
};

export const query = graphql`
  query HomePage($today: Date) {
    page: pageMdx(filter: { frontmatter: { url: { pathname: { eq: "/" } } } }) {
      id
      frontmatter {
        id
        title
        type
        description
        url {
          id
          name
          pathname
        }
        breadcrumb {
          id
          name
          pathname
        }
        releasedAt
        updatedAt
        noindex
        labels {
          id
          name
          slug
        }
      }
    }
    jobPosts: allJobPostMdx(
      filter: {
        frontmatter: {
          id: {
            in: [
              "60Trtr2vNnNQaRUgxLyv9n"
              "157yafedxmyTgKFVmldVsW"
              "2dHGdaqTjwpqSLgqGuv1qZ"
            ]
          }
        }
      }
      sort: { frontmatter: { createdAt: DESC } }
    ) {
      nodes {
        id
        frontmatter {
          id
          title
          type
          company {
            id
            slug
            name
            logo {
              imgix {
                url(imgixParams: { w: 80, h: 80, dpr: 2 })
              }
            }
            establishmentDate
            headOfficeLocation
            numberOfEmployees
            links {
              id
              title
              url
            }
          }
          locations {
            id
            name
            slug
          }
          employmentTypes {
            id
            name
            slug
          }
          categories {
            id
            name
            slug
          }
          services {
            id
            name
            slug
          }
          links {
            id
            title
            url
          }
          createdAt
          updatedAt
          closed
          isAutoGenerated
          minimumSalary
          maximumSalary
          tools
          applicationUrl
          source
        }
      }
    }
    latestArticles: allPageMdx(
      filter: {
        frontmatter: {
          type: { eq: "Article" }
          stealth: { ne: true }
          noindex: { ne: true }
        }
      }
      sort: { fields: { releasedAt: DESC } }
      limit: 6
    ) {
      nodes {
        id
        frontmatter {
          id
          title
          type
          url {
            id
            name
            pathname
          }
          updatedAt
          labels {
            id
            name
            slug
          }
          image {
            ...AssetFields
          }
        }
      }
    }
    social: collectionYaml(slug: { eq: "exploring-a-better-future" }) {
      id
      slug
      title
      subtitle
      items {
        id
        frontmatter {
          id
          title
          type
          url {
            id
            name
            pathname
          }
          updatedAt
          labels {
            id
            name
            slug
          }
          image {
            ...AssetFields
          }
        }
      }
    }
    lifestyle: collectionYaml(slug: { eq: "culture-and-lifestyle" }) {
      id
      slug
      title
      subtitle
      items {
        id
        frontmatter {
          id
          title
          type
          url {
            id
            name
            pathname
          }
          releasedAt
          updatedAt
          labels {
            id
            name
            slug
          }
          image {
            ...AssetFields
          }
        }
      }
    }
    workstyle: collectionYaml(slug: { eq: "workstyle" }) {
      id
      slug
      title
      subtitle
      items {
        id
        frontmatter {
          id
          title
          type
          url {
            id
            name
            pathname
          }
          updatedAt
          labels {
            id
            name
            slug
          }
          image {
            ...AssetFields
          }
        }
      }
    }
    design: collectionYaml(slug: { eq: "shaping-a-better-future" }) {
      id
      slug
      title
      subtitle
      items {
        id
        frontmatter {
          id
          title
          type
          url {
            id
            name
            pathname
          }
          updatedAt
          labels {
            id
            name
            slug
          }
          image {
            ...AssetFields
          }
        }
      }
    }
    latestNews: allPageMdx(
      filter: { frontmatter: { type: { eq: "News" }, noindex: { ne: true } } }
      sort: { fields: { releasedAt: DESC } }
      limit: 9
    ) {
      nodes {
        id
        excerpt
        frontmatter {
          id
          title
          type
          url {
            id
            name
            pathname
          }
          releasedAt
          updatedAt
          labels {
            id
            name
            slug
          }
          image {
            ...AssetFields
          }
        }
      }
    }
    events: allEventYaml(
      filter: { startAt: { gt: $today } }
      sort: { startAt: ASC }
      limit: 9
    ) {
      nodes {
        id
        yamlId
        title
        organizer
        startAt
        createdAt
        endAt
        venue
        url
        image {
          imgix {
            url(
              imgixParams: { w: 320, h: 180, dpr: 2, fit: "fill", fill: "blur" }
            )
          }
        }
      }
    }
  }
`;
